import { ZfBox, ZfLoadingSpinner, ZfStack, ZfTextButton } from '@zafire/react';
import moment from 'moment';
import type { FunctionComponent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useClient, useWealth } from '../../api';
import { useYwuApp } from '../../contexts/ywu-app-context';
import { getCountryName } from '../../utils/countryUtils';
import { formatAmount } from '../../utils/renderUtils';
import { transformToTitleCase } from '../../utils/utils';
import { CrossBorderWarning } from '../cross-border';

export const ClientBar: FunctionComponent = () => {
    const { t } = useTranslation();
    const { data: client } = useClient();
    const wealthData = useWealth();
    const { activeLanguage: appLanguage, isPresentationMode } = useYwuApp();
    const navigate = useNavigate();

    const handleClick = (event: MouseEvent<HTMLZfTextButtonElement>) => {
        event.stopPropagation();
        navigate('../wealth');
    };

    const gridRow = isPresentationMode ? 2 : 1;

    return (
        <>
            {isPresentationMode && (
                <ZfBox style={{ gridRow: 1, gridColumnEnd: 'span 4', alignSelf: 'center' }}>
                    <h4>{t('keyQuestionPresentationTitle')}</h4>
                </ZfBox>
            )}
            <ZfBox style={{ gridRow, gridColumnEnd: 'span 2', alignSelf: 'center' }}>
                <div className="user-dashboard__client-tile">
                    <div>
                        <label>{t('name')}</label>
                        <h5>
                            {client?.title} {client?.fullName}
                        </h5>
                    </div>
                    <div>
                        <label>{t('domicile')}</label>
                        <h5>
                            {client?.mainDomicile ? (
                                <ZfStack direction="row" spacing="extra-small" alignment="center">
                                    {getCountryName(client.mainDomicile.countryCode, appLanguage)}
                                    <CrossBorderWarning variant="popover" />
                                </ZfStack>
                            ) : null}
                        </h5>
                    </div>
                    <div>
                        <label>{t('clientAge')}</label>
                        <h5>{moment().diff(client?.birthdate, 'years') || ''}</h5>
                    </div>
                    <div>
                        <label>{t('clientChildren')}</label>
                        <h5>{client?.childRelations?.length}</h5>
                    </div>
                    <div>
                        <label>{t('clientStatus')}</label>
                        <h5>{transformToTitleCase(client?.maritalStatus[appLanguage])}</h5>
                    </div>
                </div>
            </ZfBox>
            <ZfBox style={{ gridRow, gridColumnEnd: 'span 2', alignSelf: 'center' }}>
                {wealthData.isLoading ? (
                    <ZfLoadingSpinner />
                ) : (
                    <div className="user-dashboard__total-assets">
                        <div>
                            <label>{t('wealthTotalAssets')}</label>
                            <h5>
                                {client?.currency ?? ''} {formatAmount(wealthData?.assetsTotal ?? 0)}
                            </h5>
                        </div>
                        <div>
                            <label>{t('wealthTotalAssetsWithJB')}</label>
                            <h5>
                                {client?.currency ?? ''} {formatAmount(wealthData?.jbAssets ?? 0)}
                            </h5>
                        </div>
                        <ZfTextButton size="medium" hierarchy="secondary" onClick={handleClick}>
                            {t('buttonTotalWealth')}
                        </ZfTextButton>
                    </div>
                )}
            </ZfBox>
        </>
    );
};
